// ============================================
// Preact Signals State Management
// ============================================

import { signal, computed, effect, batch } from '@preact/signals';
import type {
  JLPTLevel,
  Mode,
  QuizMode,
  Word,
  WordProgress,
  WordProgressMap,
  SRSCategory,
  SRSState,
  Notification
} from '@/types';
import { storageService, audioService } from '@/services';
import { getDeviceId } from '@/services/deviceId';
import { normalizeAttempts } from '@/services/mergeProgress';
import { SRS_CONFIG } from '@/constants';
import { buildDueDeck } from './dueDeck';

export function categoryFor(state: SRSState): SRSCategory {
  if (state === 'new') return 'new';
  if (state === 'mastered') return 'mastered';
  if (state.startsWith('learning')) return 'learning';
  return 'review';
}

/**
 * A bucket the list modal can show: one of the four SRS categories, or the
 * derived 'again' view (words last marked ✗ Again — see `againWords`).
 */
export type BucketView = SRSCategory | 'again';

// Wire the storage queue's source-of-truth reader to the wordProgress
// signal value. The queue captures (level, japanese) tuples but reads
// the full map at flush time, so the reader returns whatever the signal
// currently holds for the currently-active level — older levels that
// were enqueued before a level switch read from localStorage instead
// (their progress is no longer in the signal).
storageService.setProgressReader(level => {
  if (level === currentLevel.value) return wordProgress.value;
  return storageService.getWordProgress(level);
});

// Surface storage failures during pagehide/timer-driven flushes as a
// one-time toast instead of letting them throw into the void.
storageService.setFlushErrorHandler(err => notifyStorageDegraded(err));

// ============================================
// Core Application State
// ============================================

// Level & Mode
export const currentLevel = signal<JLPTLevel>('N5');
export const currentMode = signal<Mode>('study');
export const currentQuizMode = signal<QuizMode>('multiple-choice');

// Deck & Navigation
//
// `deck` holds the full vocabulary for the active level — quiz mode walks
// it in shuffle/insertion order. `dueDeck` is the SRS-aware queue rebuilt
// at session start and on level change (NOT a computed that re-derives
// on every wordProgress write); study mode walks it.
export const deck = signal<Word[]>([]);
export const dueDeck = signal<Word[]>([]);
export const cardIndex = signal(0);
export const isFlipped = signal(false);
export const navigationDirection = signal(1); // 1 = forward, -1 = backward

// Focused "Drill these" practice session. When `drillDeck` is non-null study
// mode walks it instead of `dueDeck`, the grade buttons are hidden, and nav
// wraps (loops the set). PRACTICE = no grading: the drill never calls
// updateWordSRS, so cramming Review/Again words can't inflate mastery.
// `drillLevel` captures the level at drill start so a cross-tab write for a
// DIFFERENT level can end the drill while same-level writes leave it intact.
export const drillDeck = signal<Word[] | null>(null);
export const drillCategory = signal<BucketView | null>(null);
export const drillLevel = signal<JLPTLevel | null>(null);

// Quiz State
export const quizAnswered = signal(false);
// Session-progress counter: how many quiz questions have been ANSWERED (not
// merely navigated past) in the current quiz session. useQuizAnswer bumps it as
// it auto-advances; QuizMode derives session completion from it and resets it to
// 0 at each "Start Quiz". Plain arrow navigation calls nextCard() directly and
// never bumps this, so browsing the deck can't consume a session's questions.
export const quizAnsweredCount = signal(0);
// Wrap bound for quiz-source navigation during an ACTIVE session-controls
// quiz. QuizMode sets it to the sliced session length at "Start Quiz" and
// clears it (null) when the session ends — while set, nextCard/prevCard wrap
// within the slice instead of the full deck, so arrow navigation can't show
// "Question 21 of 20" or surface cards outside the chosen session. null
// (flag off / no active session) means wrap over the full deck as always.
export const quizWrapLimit = signal<number | null>(null);
export const kanaMode = signal(false);

// Word Progress (SRS data)
export const wordProgress = signal<WordProgressMap>({});

// SRS bucket counters — incrementally maintained in updateWordSRS so we
// don't iterate the entire ~600-entry progress map on every answer.
// These replace the previous `srsStats` computed that scanned all entries
// (and re-rendered every subscriber on every answer).
//
// The `_set` helper rebuilds counts from a progress map; called from
// hydrateProgressForLevel and resetProgress where the whole map is
// replaced atomically.
export const newCount = signal(0);
export const learningCount = signal(0);
export const reviewCount = signal(0);
export const masteredCount = signal(0);

// Cloud-sync state (Group C/D). `currentUser` holds the Supabase user id
// when signed in, null when anonymous. `syncStatus` reflects the last
// sync outcome for any future UI affordance. Both are reset by
// resetAppState() and by SyncService on signout/user-change. The expected
// `map_version` per level is held INSIDE SyncService (not a signal) — it's
// an implementation detail of the orchestrator, not shared UI state.
export const currentUser = signal<string | null>(null);
export const syncStatus = signal<'idle' | 'syncing' | 'error' | 'offline'>('idle');

// Session-scoped controls — reset on level/deck change, not on every answer.
// `sessionNewCardLimit` overrides the global SRS_CONFIG.newCardsPerSession for
// the current session; `studyMore()` increments it by one batch.
// `quizLength` caps how many cards a quiz session surfaces (null = All).
export const sessionNewCardLimit = signal(SRS_CONFIG.newCardsPerSession);
export const quizLength = signal<number | null>(20);

function recountBuckets(progress: WordProgressMap): {
  new: number;
  learning: number;
  review: number;
  mastered: number;
} {
  const counts = { new: 0, learning: 0, review: 0, mastered: 0 };
  for (const p of Object.values(progress)) {
    counts[categoryFor(p.state || 'new')]++;
  }
  return counts;
}

function syncCountsFromProgress(progress: WordProgressMap): void {
  const counts = recountBuckets(progress);
  batch(() => {
    newCount.value = counts.new;
    learningCount.value = counts.learning;
    reviewCount.value = counts.review;
    masteredCount.value = counts.mastered;
  });
}

/**
 * Replace `wordProgress` with `map` and sync the four counter signals
 * atomically. Use this for any path that swaps the whole progress map
 * (boot, level switch, reset, cross-tab storage event). The four
 * counter signals are derived state — every map-replacing write must
 * pair with a count sync, or counters drift and masteryPercent goes
 * stale. updateWordSRS uses incremental ±1 updates and does NOT go
 * through this helper.
 */
export function setWordProgressMap(map: WordProgressMap): void {
  batch(() => {
    wordProgress.value = map;
    syncCountsFromProgress(map);
  });
}

// Session Stats
export const cardsStudied = signal<Set<string>>(new Set());
export const quizCorrect = signal(0);
export const quizIncorrect = signal(0);

// UI State
// statsVisible persists open/closed state across sessions so a user who
// opened the Stats panel keeps it open on next visit. Hydrated from
// localStorage on module init; changes written back via a module-level
// effect. Both operations are wrapped in try/catch for private-mode safety
// (localStorage may throw in jsdom or restricted environments).
const _statsVisibleStored = (() => {
  try {
    const raw = localStorage.getItem('jlpt-stats-visible');
    return raw !== null ? (JSON.parse(raw) as boolean) : false;
  } catch {
    return false;
  }
})();
export const statsVisible = signal<boolean>(_statsVisibleStored);
effect(() => {
  try {
    localStorage.setItem('jlpt-stats-visible', JSON.stringify(statsVisible.value));
  } catch {
    // Private-mode or quota — silently skip; resetAppState still works.
  }
});
export const menuOpen = signal(false);

// Notifications
export const notifications = signal<Notification[]>([]);

// Audio State
export const audioPlaying = signal(false);
export const audioLoading = signal(false);

// ============================================
// Computed Values
// ============================================

// Active deck for the current mode: study walks dueDeck (SRS-ordered) — or the
// drill deck when a focused practice session is active — quiz walks the full
// shuffled deck.
export const activeDeck = computed(() =>
  currentMode.value === 'study' ? (drillDeck.value ?? dueDeck.value) : deck.value
);

// Current card
export const currentCard = computed(() => {
  const d = activeDeck.value;
  const idx = cardIndex.value;
  return d.length > 0 && idx >= 0 && idx < d.length ? d[idx] : null;
});

// Current word's SRS state
export const currentSRSState = computed<SRSState>(() => {
  const card = currentCard.value;
  if (!card) return 'new';

  const progress = wordProgress.value[card.japanese];
  return progress?.state || 'new';
});

// Progress percentage (position in active deck)
export const progressPercent = computed(() => {
  const d = activeDeck.value;
  const idx = cardIndex.value;
  return d.length > 0 ? ((idx + 1) / d.length) * 100 : 0;
});

// Total words in current level
export const totalWords = computed(() => deck.value.length);

// Mastery percentage — derived from incremental counter signals so it
// only recomputes when a card's state actually changes (not on every
// wordProgress write that doesn't move a card across buckets).
export const masteryPercent = computed(() => {
  // Denominator is the whole level (deck length), not just materialized
  // progress entries — otherwise a user who has touched 1 word and mastered
  // it would read 100%. Unstarted words count toward the level total.
  const total = deck.value.length;
  return total > 0 ? Math.round((masteredCount.value / total) * 100) : 0;
});

// Display count of "New" cards for the active level. newCount only tracks
// words with a materialized 'new' progress entry; unstarted words have no
// entry at all (we lazy-create on first grade). So the user-facing "New"
// number is the level total minus everything that has progressed past new.
export const newDisplay = computed(() =>
  Math.max(
    0,
    deck.value.length -
      learningCount.value -
      reviewCount.value -
      masteredCount.value
  )
);

// Lifetime reviews completed across the active level — the sum of
// totalAttempts over every word's progress entry. Derived from
// wordProgress (already persisted AND cloud-synced), so it survives a
// reload where the ephemeral "this session" counters reset to 0. Tolerates
// missing/partial entries (an entry without totalAttempts contributes 0).
export const reviewsDone = computed(() => {
  let total = 0;
  for (const p of Object.values(wordProgress.value)) {
    total += p?.totalAttempts ?? 0;
  }
  return total;
});

// Lifetime study accuracy (0–1) across the active level: total correct
// attempts / total attempts. Guards divide-by-zero → 0. Like reviewsDone
// this derives from persisted/synced wordProgress, not session state.
export const studyAccuracy = computed(() => {
  let attempts = 0;
  let correct = 0;
  for (const p of Object.values(wordProgress.value)) {
    attempts += p?.totalAttempts ?? 0;
    correct += p?.correctAttempts ?? 0;
  }
  return attempts > 0 ? correct / attempts : 0;
});

// ============================================
// Bucket / "Again" word-list selectors
// ============================================
//
// Power the tappable stat-card lists (BucketListModal). Pure functions over
// deck + wordProgress so they recompute reactively when called inside a
// component render. Both share one struggle-sort comparator: worst accuracy
// first (drill the words you fail most), then soonest due, then a stable
// japanese tiebreak so equal-rank rows never reshuffle between renders.

function byStruggle(prog: WordProgressMap): (a: Word, b: Word) => number {
  return (a, b) => {
    const pa = prog[a.japanese];
    const pb = prog[b.japanese];
    // Unattempted → accuracy 1 (sorts last); only reachable for the New bucket
    // since any graded word has totalAttempts > 0.
    const accA = pa && pa.totalAttempts > 0 ? pa.correctAttempts / pa.totalAttempts : 1;
    const accB = pb && pb.totalAttempts > 0 ? pb.correctAttempts / pb.totalAttempts : 1;
    if (accA !== accB) return accA - accB;
    const dr = (pa?.nextReview ?? 0) - (pb?.nextReview ?? 0);
    if (dr !== 0) return dr;
    return a.japanese.localeCompare(b.japanese);
  };
}

/**
 * Words in the active level that fall in `category`, struggle-sorted. New
 * words have no progress entry — `?? 'new'` routes them to the New bucket.
 */
export function bucketWords(category: SRSCategory): Word[] {
  const prog = wordProgress.value;
  return deck.value
    .filter(w => categoryFor(prog[w.japanese]?.state ?? 'new') === category)
    .sort(byStruggle(prog));
}

/**
 * The "Again" view: words the user last marked ✗ Again. `updateWordSRS` zeroes
 * `correctStreak` on a wrong answer, so `correctStreak === 0 && totalAttempts > 0`
 * identifies attempted-and-currently-failing words (spans Learning/Review).
 */
export function againWords(): Word[] {
  const prog = wordProgress.value;
  return deck.value
    .filter(w => {
      const p = prog[w.japanese];
      return !!p && p.totalAttempts > 0 && p.correctStreak === 0;
    })
    .sort(byStruggle(prog));
}

/**
 * Count for the "Again / Needs work" chip. Derived from `againWords()` (not a
 * raw scan of wordProgress) so the chip number always equals the modal list
 * length — an orphaned progress entry for a word no longer in the deck can't
 * inflate the chip above what the list renders.
 */
export const againCount = computed(() => againWords().length);

// ============================================
// Actions
// ============================================

export function setLevel(level: JLPTLevel) {
  // Flush any pending writes for the OUTGOING level before switching.
  // Without this, a queued write enqueued under N5 could land *after*
  // the level switched to N4 — but per-entry level capture means it
  // would be written under N5's key correctly. The flush here is for
  // the secondary guarantee that no in-flight writes overlap the
  // hydrate-for-new-level read.
  storageService.flushPending();
  // A drill is scoped to one level's words — end it before switching so the
  // new level's deck doesn't render under the old drill set.
  exitDrill();
  currentLevel.value = level;
  // App.tsx's level-load effect handles the rest: load vocab →
  // hydrateProgressForLevel → setDeck (which calls rebuildDueDeck).
}

export function setMode(mode: Mode) {
  // Leaving study ends any active drill — practice is study-only, with no
  // invisible "paused" state when the user switches to Quiz.
  if (mode !== 'study') exitDrill();
  currentMode.value = mode;
  isFlipped.value = false;
  quizAnswered.value = false;
  // Reset position to start of the new active deck.
  cardIndex.value = 0;
}

/**
 * Start a focused PRACTICE session over a bucket. `view` is 'review' or
 * 'again'. The drill walks a struggle-sorted copy of those words with the
 * grade buttons hidden (StudyMode), so it never advances SRS.
 */
export function enterDrill(view: BucketView) {
  const words = view === 'again' ? againWords() : bucketWords(view);
  batch(() => {
    drillCategory.value = view;
    drillLevel.value = currentLevel.value;
    drillDeck.value = words;
    currentMode.value = 'study';
    cardIndex.value = 0;
    isFlipped.value = false;
  });
}

/** End the practice session and fall back to the normal study queue. No-op when inactive. */
export function exitDrill() {
  if (drillDeck.value === null) return;
  batch(() => {
    drillDeck.value = null;
    drillCategory.value = null;
    drillLevel.value = null;
    cardIndex.value = 0;
    isFlipped.value = false;
  });
}

export function setQuizMode(mode: QuizMode) {
  currentQuizMode.value = mode;
  quizAnswered.value = false;
}

export function setDeck(words: Word[]) {
  deck.value = words;
  cardIndex.value = 0;
  isFlipped.value = false;
  // Reset the session new-card limit so a level change starts fresh.
  // Must happen BEFORE rebuildDueDeck() so the reset value is used in the
  // first build of the new level's due queue.
  sessionNewCardLimit.value = SRS_CONFIG.newCardsPerSession;
  rebuildDueDeck();
  // Warm audio for the first few cards of the fresh deck (fire-and-forget).
  audioService.preloadForDeck(words);
}

/**
 * Rebuild the SRS-aware dueDeck from the current vocabulary + progress.
 * Called at session start, on level change, and after `setDeck`. NOT
 * called on every `updateWordSRS` — that would re-sort the queue on every
 * answer (the regression class W2 is fixing for srsStats).
 */
export function rebuildDueDeck() {
  dueDeck.value = buildDueDeck(deck.value, wordProgress.value, Date.now(), {
    newCardsPerSession: sessionNewCardLimit.value,
    deckSize: SRS_CONFIG.deckSize
  });
}

/**
 * Expand the current session's new-card allowance by one batch
 * (SRS_CONFIG.newCardsPerSession cards). Rebuilds dueDeck immediately so
 * newly eligible cards appear without a page reload.
 */
export function studyMore() {
  sessionNewCardLimit.value += SRS_CONFIG.newCardsPerSession;
  rebuildDueDeck();
  // Rewind to the start of the freshly rebuilt queue. Without this, a user
  // who reached the empty state by GRADING the batch keeps cardIndex at the
  // old out-of-range sentinel; after the rebuild (graded cards drop out as
  // not-yet-due) that index can still be past the end → straight back to the
  // empty state. Starting at 0 lands them on the first card of the next batch.
  batch(() => {
    cardIndex.value = 0;
    isFlipped.value = false;
  });
}

/**
 * Replace `wordProgress` with the persisted state for the given level.
 * Called on app boot for the initial level and on every level change
 * (Finding H — without this the signal silently retains the previous
 * level's progress in-memory).
 */
export function hydrateProgressForLevel(level: JLPTLevel) {
  setWordProgressMap(storageService.getWordProgress(level));
}

export interface NavOptions {
  /**
   * Override which deck to advance through. Defaults to whichever is
   * active for `currentMode` — study uses dueDeck, quiz uses deck.
   */
  source?: Mode;
}

/** The deck nav should walk for a given mode: drill set (if active) > dueDeck (study) > deck (quiz). */
function navDeck(mode: Mode): Word[] {
  if (mode === 'study' && drillDeck.value) return drillDeck.value;
  return mode === 'study' ? dueDeck.value : deck.value;
}

// Effective wrap length for quiz-source navigation: the active session
// slice when a session-controls quiz is running (quizWrapLimit), else the
// full deck. Clamped to the deck so a stale/oversized limit can't strand
// cardIndex past real content.
function quizNavLength(deckLength: number): number {
  const limit = quizWrapLimit.value;
  return limit !== null ? Math.min(limit, deckLength) : deckLength;
}

export function nextCard(options?: NavOptions) {
  const mode = options?.source ?? currentMode.value;
  const drilling = mode === 'study' && drillDeck.value !== null;
  const d = navDeck(mode);
  if (d.length === 0) return;

  navigationDirection.value = 1;
  if (mode === 'study' && !drilling) {
    // Study mode does NOT wrap. Advancing past the last card moves
    // cardIndex to an out-of-range sentinel (== deck length) so
    // currentCard computes to null and StudyMode renders its
    // "All caught up!" empty state. prevCard from the sentinel returns
    // to the last card (cardIndex - 1) so back-navigation still works.
    cardIndex.value = Math.min(cardIndex.value + 1, d.length);
  } else {
    // Quiz mode AND drill practice wrap so the set never ends mid-session —
    // within the session slice when one is active (see quizWrapLimit).
    const len = mode === 'quiz' ? quizNavLength(d.length) : d.length;
    cardIndex.value = (cardIndex.value + 1) % len;
  }
  isFlipped.value = false;
  quizAnswered.value = false;
  // Warm the TTS cache for upcoming cards so the first audio tap doesn't
  // block on the full TTS round trip. Fire-and-forget: dedupes via the
  // audio cache and no-ops offline / circuit-open.
  audioService.preloadNext(cardIndex.value, d);
}

export function prevCard(options?: NavOptions) {
  const mode = options?.source ?? currentMode.value;
  const d = navDeck(mode);
  if (d.length === 0) return;

  navigationDirection.value = -1;
  const len = mode === 'quiz' ? quizNavLength(d.length) : d.length;
  cardIndex.value = cardIndex.value > 0 ? cardIndex.value - 1 : len - 1;
  isFlipped.value = false;
  quizAnswered.value = false;
}

export function flipCard() {
  isFlipped.value = !isFlipped.value;

  if (isFlipped.value) {
    const card = currentCard.value;
    if (card) {
      cardsStudied.value = new Set([...cardsStudied.value, card.japanese]);
    }
  }
}

export function shuffleDeck() {
  const d = [...deck.value];
  for (let i = d.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [d[i], d[j]] = [d[j], d[i]];
  }
  deck.value = d;
  cardIndex.value = 0;
  isFlipped.value = false;
}

export function toggleStats() {
  statsVisible.value = !statsVisible.value;
}

export function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}

export function closeMenu() {
  menuOpen.value = false;
}

export function toggleKana() {
  kanaMode.value = !kanaMode.value;
}

// ============================================
// SRS Actions
// ============================================

export function updateWordSRS(japanese: string, isCorrect: boolean) {
  const now = Date.now();

  // Lazy-create: unstarted words have no progress entry (we don't seed the
  // whole level up-front). The first grade materializes a 'new' entry so the
  // transition records — without this, a new user's first answer in EITHER
  // quiz or study mode was silently dropped (updateWordSRS used to early-
  // return on a missing entry).
  const existing = wordProgress.value[japanese];
  const current: WordProgress = existing ?? {
    state: 'new',
    lastReviewed: null,
    nextReview: now,
    correctStreak: 0,
    totalAttempts: 0,
    correctAttempts: 0,
    created: now,
    cardIndex: 0
  };

  const currentState = current.state || 'new';
  const nextState =
    SRS_CONFIG.progressions[currentState][isCorrect ? 'correct' : 'incorrect'];
  const interval = SRS_CONFIG.intervals[nextState];

  // Device-attributed attempt buckets (see spec-counter-merge.md): normalize
  // first — this is the lazy migration that folds a pre-migration record's
  // flat counters into the `legacy` bucket — then increment ONLY this
  // device's bucket (never `legacy`; only the owning device grows its bucket,
  // which is what makes the sync merge's per-bucket max exact). The flat
  // counters stay as derived sums so UI reads (reviewsDone, studyAccuracy,
  // againWords sort) need no change; incrementing the normalized sums keeps
  // the flat == Σ buckets invariant in the same record write.
  const { attempts, totalAttempts, correctAttempts } = normalizeAttempts(current);
  const deviceId = getDeviceId();
  const bucket = attempts[deviceId] ?? { t: 0, c: 0 };
  attempts[deviceId] = {
    t: bucket.t + 1,
    c: bucket.c + (isCorrect ? 1 : 0)
  };

  const updated = {
    ...current,
    state: nextState,
    lastReviewed: now,
    nextReview: now + interval,
    attempts,
    totalAttempts: totalAttempts + 1,
    correctAttempts: correctAttempts + (isCorrect ? 1 : 0),
    correctStreak: isCorrect ? current.correctStreak + 1 : 0
  };

  // Compute incremental counter delta BEFORE we mutate the map.
  const fromBucket = categoryFor(currentState);
  const toBucket = categoryFor(nextState);

  // Single batched notification: counter signals + wordProgress write.
  // Subscribers see one consistent state transition.
  batch(() => {
    if (!existing) {
      // Newly materialized: the word was an unstarted 'new' card not tracked
      // in newCount, so only credit the destination bucket (don't decrement).
      incrementBucket(toBucket);
    } else if (fromBucket !== toBucket) {
      decrementBucket(fromBucket);
      incrementBucket(toBucket);
    }
    wordProgress.value = { ...wordProgress.value, [japanese]: updated };
  });

  // Persist via the debounced queue. Wrapped in try/catch for graceful
  // degradation when localStorage is disabled (private browsing).
  try {
    storageService.queueWordProgressWrite(currentLevel.value, japanese);
  } catch (err) {
    notifyStorageDegraded(err);
  }
}

function incrementBucket(bucket: SRSCategory): void {
  if (bucket === 'new') newCount.value++;
  else if (bucket === 'learning') learningCount.value++;
  else if (bucket === 'review') reviewCount.value++;
  else masteredCount.value++;
}

function decrementBucket(bucket: SRSCategory): void {
  if (bucket === 'new') newCount.value = Math.max(0, newCount.value - 1);
  else if (bucket === 'learning')
    learningCount.value = Math.max(0, learningCount.value - 1);
  else if (bucket === 'review')
    reviewCount.value = Math.max(0, reviewCount.value - 1);
  else masteredCount.value = Math.max(0, masteredCount.value - 1);
}

/**
 * Grade the current study card and advance. This is the bridge that makes
 * Study mode drive the SRS — previously updateWordSRS was only called from
 * the six quiz components, so flashcard study never moved a card's state.
 *
 * Reuses the existing updateWordSRS hot path (incremental bucket counters +
 * debounced persist). Deliberately does NOT call rebuildDueDeck() — the
 * dueDeck is a cached signal built at session start / level change (W2 perf
 * boundary, see rebuildDueDeck above). Advancing by index only means a card
 * that just matured won't re-enter the queue until next session, matching
 * Anki's "queue computed at session start" model. Advance uses nextCard's
 * study path (no wrap), so grading the last card drains the deck into the
 * "All caught up!" empty state.
 */
export function gradeStudyCard(isCorrect: boolean) {
  // PRACTICE guard: during a drill, grading is OFF regardless of entry point
  // (hidden buttons can't be clicked, but the 1/2 study hotkeys still reach
  // here). Gating the single chokepoint keeps the no-grade contract airtight —
  // just advance through the practice set without touching SRS.
  if (drillDeck.value !== null) {
    nextCard({ source: 'study' });
    return;
  }
  const card = currentCard.value;
  if (card) {
    updateWordSRS(card.japanese, isCorrect);
  }
  nextCard({ source: 'study' });
}

// Private-mode graceful degradation: when localStorage throws (disabled
// in private browsing on some browsers, quota exceeded, etc.), fire a
// one-time toast so the user knows progress isn't persisting.
let storageDegradedNotified = false;
function notifyStorageDegraded(err: unknown): void {
  if (storageDegradedNotified) return;
  storageDegradedNotified = true;
  console.warn('Storage write failed; progress will not persist this session', err);
  showNotification(
    "Progress won't save — open a regular window to keep your progress.",
    'warning',
    6000
  );
}

// Sync actionable-error toast: surfaces a user-visible nudge when sync
// fails for a reason the user can fix (session expired / RLS denial).
// One-time-ish per session so a flapping auth error doesn't spam toasts;
// the guard is independent of the storage-degraded guard so an actionable
// sync failure is never swallowed by an earlier transient/storage toast.
// Reset in resetAppState() for test isolation.
let syncActionableNotified = false;
export function notifySyncActionable(
  message = 'Session expired — sign in to resume sync'
): void {
  if (syncActionableNotified) return;
  syncActionableNotified = true;
  console.warn('[Sync] actionable failure:', message);
  showNotification(message, 'error', 6000);
}

export function resetProgress() {
  const empty: WordProgressMap = {};
  deck.value.forEach((word, index) => {
    empty[word.japanese] = {
      state: 'new',
      lastReviewed: null,
      nextReview: Date.now(),
      correctStreak: 0,
      totalAttempts: 0,
      correctAttempts: 0,
      created: Date.now(),
      cardIndex: index
    };
  });

  batch(() => {
    setWordProgressMap(empty);
    cardsStudied.value = new Set();
    quizCorrect.value = 0;
    quizIncorrect.value = 0;
    // End any active drill — its banner shouldn't linger over a wiped session.
    exitDrill();
    // Rebuild the SRS queue from the now-empty progress and rewind to the
    // first card. Without these the dueDeck and cardIndex were left stale
    // after a reset (e.g. pointing past the end, or holding pre-reset
    // card ordering), so Study mode could show "All caught up!" or a wrong
    // card immediately after the user reset their progress.
    rebuildDueDeck();
    cardIndex.value = 0;
  });
  try {
    storageService.saveWordProgress(currentLevel.value, empty);
  } catch (err) {
    notifyStorageDegraded(err);
  }
}

// ============================================
// Notification Actions
// ============================================

let notificationId = 0;

export function showNotification(
  message: string,
  type: Notification['type'] = 'info',
  duration = 3000
) {
  const id = `notification-${++notificationId}`;
  const notification: Notification = { id, message, type, duration };

  notifications.value = [...notifications.value, notification];

  if (duration > 0) {
    setTimeout(() => {
      dismissNotification(id);
    }, duration);
  }

  return id;
}

export function dismissNotification(id: string) {
  notifications.value = notifications.value.filter(n => n.id !== id);
}

// ============================================
// Persistence Effects
// ============================================

// Hydrate level + kanaMode from storage into their signals. Defined and
// CALLED before the auto-save effect below is registered — see the ordering
// note there.
export function loadPreferences() {
  const prefs = storageService.getPreferences({
    level: 'N5',
    kanaMode: false
  });

  currentLevel.value = prefs.level as JLPTLevel;
  kanaMode.value = prefs.kanaMode as boolean;
}

// ORDERING CONSTRAINT: hydrate BEFORE registering the auto-save effect.
// A preact `effect` runs its body once synchronously at registration to
// capture dependencies. If that first run observed the default currentLevel
// ('N5'), it would savePreferences({level:'N5'}) and clobber the stored
// preference before anything read it — every returning user would boot into
// N5. Loading first means the effect's initial run writes back the already-
// hydrated value (a no-op), and only genuine user changes persist thereafter.
loadPreferences();

// Auto-save preferences when they change.
effect(() => {
  storageService.savePreferences({
    level: currentLevel.value,
    kanaMode: kanaMode.value
  });
});

// ============================================
// Test helper: reset every signal to its boot default
// ============================================
//
// Module-scope signals retain state across tests. resetAppState() restores
// every Signal export to its initial value. Tests should call this in
// beforeEach (wired via src/test-setup.ts).
//
// Adding a new signal export without updating this function will fail the
// resetAppState meta-test (src/state/__tests__/resetAppState.test.ts) by
// design — that's the enforcement.
export function resetAppState(): void {
  // Drain any in-flight queued storage writes from the previous test.
  // Without this, debounced timers fire mid-next-test and produce
  // nondeterministic localStorage state.
  storageService.flushPending();

  // Level & Mode
  currentLevel.value = 'N5';
  currentMode.value = 'study';
  currentQuizMode.value = 'multiple-choice';

  // Deck & Navigation
  deck.value = [];
  dueDeck.value = [];
  cardIndex.value = 0;
  isFlipped.value = false;
  navigationDirection.value = 1;

  // Drill (focused practice) state
  drillDeck.value = null;
  drillCategory.value = null;
  drillLevel.value = null;

  // Quiz State
  quizAnswered.value = false;
  quizAnsweredCount.value = 0;
  quizWrapLimit.value = null;
  kanaMode.value = false;

  // Word Progress (SRS data)
  wordProgress.value = {};
  newCount.value = 0;
  learningCount.value = 0;
  reviewCount.value = 0;
  masteredCount.value = 0;

  // Session controls
  sessionNewCardLimit.value = SRS_CONFIG.newCardsPerSession;
  quizLength.value = 20;

  // Cloud-sync state
  currentUser.value = null;
  syncStatus.value = 'idle';

  // Reset the one-time storage-degraded toast flag so the next test
  // can re-trigger it cleanly.
  storageDegradedNotified = false;

  // Reset the one-time sync actionable-error toast flag too.
  syncActionableNotified = false;

  // Session Stats
  cardsStudied.value = new Set();
  quizCorrect.value = 0;
  quizIncorrect.value = 0;

  // UI State
  statsVisible.value = false;
  menuOpen.value = false;

  // Notifications
  notifications.value = [];

  // Audio State
  audioPlaying.value = false;
  audioLoading.value = false;
}
